import { Box, Button, Checkbox, Grid, ListItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { BestMixFeed } from 'views/feed-management/features/best-mix-feeds/types/best-mix-feed.type';
import { CustomerProducts } from 'views/feed-management/features/customers/types/customer-products.type';
import { UpdateCustomerProductsInput } from 'views/feed-management/features/customers/types/update-customer-products-input.type';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import { AxiosResponse } from 'axios';
import Autocomplete from 'components/autocomplete';
import Breadcrumbs from 'components/breadcrumbs';
import Dialog from 'components/dialog';
import Error from 'components/error';
import FlexBox from 'components/flex-box';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Pagination from 'components/pagination';

import Paper from 'components/paper';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import { usePagination } from 'hooks/use-pagination';
import { AlertTriangle, Square, SquareCheckBig } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import _ from 'underscore';
import { getFeedManagementCustomerProducts } from 'views/feed-management/features/customers/api/get-customers';
import {
  putDisconnectCustomerFeeds,
  putFeedManagementCustomerProducts,
} from 'views/feed-management/features/customers/api/update-customers';
import { UpdateCustomerDisconnectFeedsInput } from 'views/feed-management/features/customers/types/update-customer-disconnect-feeds-input.type';
import FeedManagementTabsHeader from '../../../components/feed-management-tabs-header';
import { getActiveFeedManagementBestMixFeeds } from '../../best-mix-feeds/api/get-best-mix-feeds';

type ParamTypes = {
  id: string;
};

type FormValues = {
  product: BestMixFeed;
  customerPricePerTone: number;
};

type Props = {};

const PAGINATION_LIMIT = 10;

const EditCustomer: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();

  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const disconnectFeedsDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const [selectedFeeds, setSelectedFeeds] = useState<string[]>([]);
  const [selectAllFeeds, setSelectAllFeeds] = useState<Record<number, boolean>>({});
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);

  const PAGE_NAME_ID = 'feedManagement.editCustomer';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // FORMS
  const { handleSubmit, control } = useForm<FormValues>({
    defaultValues: {
      product: undefined,
      customerPricePerTone: 0,
    },
    shouldUnregister: true,
  });

  // STATE

  // FETCH DATA
  const {
    data: customerProducts,
    error,
    isFetching,
    isError,
  } = useGet(
    ['getFeedManagementCustomerProducts', params.id],
    (context) => getFeedManagementCustomerProducts(String(context.queryKey[1])),
    {
      enabled: !!params.id,
    }
  );
  const { data: products } = useGet(['getActiveFeedManagementBestMixFeeds'], () => getActiveFeedManagementBestMixFeeds());

  // POST DATA
  const linkCustomer = usePost<CustomerProducts, UpdateCustomerProductsInput[]>(
    (request) => putFeedManagementCustomerProducts(params.id, request),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      dialogRef.current?.setOpen(false);
      queryClient.setQueryData(['getFeedManagementCustomerProducts', params.id], response);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const disconnectBestMixFeeds = usePost<AxiosResponse, { globalCompanyId: string; productCodes: UpdateCustomerDisconnectFeedsInput }>(
    (request) => putDisconnectCustomerFeeds(request.globalCompanyId, request.productCodes),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      disconnectFeedsDialogRef.current?.setOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['getFeedManagementCustomerProducts']
      });
      resetStates();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDLERS

  const resetStates = () => {
    setSelectAllFeeds({});
    setSelectedFeeds([]);
  };

  const handleDisconnectBestMixFeeds = () => {
    const codes: UpdateCustomerDisconnectFeedsInput = {
      productCodes: selectedFeeds,
    };

    disconnectBestMixFeeds.mutate({ globalCompanyId: params.id, productCodes: codes });
  };

  const handleLinkCustomer = (data: FormValues) => {
    linkCustomer.mutate([
      ...customerProducts!.assignedProductsDetails?.map((item) => ({
        productCode: item.code,
        customerPricePerTone: item.customerPricePerTon,
      })),
      {
        productCode: data.product.code,
        customerPricePerTone: data.customerPricePerTone,
      },
    ]);
  };

  const handleUnlinkProduct = (code: string) => {
    linkCustomer.mutate(
      customerProducts!.assignedProductsDetails
        ?.filter((item) => item.code !== code)
        .map((item) => ({
          productCode: item.code,
          customerPricePerTone: item.customerPricePerTon,
        }))
    );
  };

  const handleSelectAllFeeds = (pageNumber: number, checked: boolean) => {
    const feedsOnPage = filteredRows.slice(from, to);
    const allFeedsIdsOnPage = feedsOnPage.map((product) => product.code);
    const updatedSelectedFeeds = checked
      ? [...selectedFeeds, ...allFeedsIdsOnPage]
      : selectedFeeds.filter((id) => !allFeedsIdsOnPage.includes(id));
    setSelectedFeeds(updatedSelectedFeeds);
    setSelectAllFeeds((prevSelectAllFeeds) => ({ ...prevSelectAllFeeds, [pageNumber]: checked }));
  };

  const handleSelectFeeds = (value: string, checked: boolean) => {
    setSelectedFeeds(checked ? selectedFeeds.concat([value]) : selectedFeeds.filter((s) => s !== value));
  };

  // RENDERS
  const renderSelectedFeeds = (selectedFeeds: string[]) => {
    const filteredProducts = customerProducts!.assignedProductsDetails.filter((u) => selectedFeeds.includes(u.code));
    return (
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} wrap="wrap" overflow={'auto'} maxHeight={'300px'}>
        {filteredProducts.map((product) => (
          <Grid size={6} key={`${product.code}`}>
            <ListItem sx={{ py: 1, px: 2, borderBottom: '1px solid #ddd' }}>
              <Typography variant="body1">{product?.description}</Typography>
            </ListItem>
          </Grid>
        ))}
      </Grid>
    );
  };

  const filteredRows = useMemo(() => {
    if (!customerProducts || !customerProducts.assignedProductsDetails) return [];
    return _.chain(customerProducts.assignedProductsDetails).value();
  }, [customerProducts]);

  useEffect(() => {
    handlePagination(1);
  }, [filteredRows]);

  return isError ? (
    <Error error={error} />
  ) : !customerProducts || isFetching ? (
    <LoadingSpinner />
  ) : (
    <>
      <FeedManagementTabsHeader
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: customerProducts.name,
                url: customerProducts.id,
              },
            ]}
          />
        }
      />
      <Paper transparent>
        <Typography variant="h3" color={theme.palette.common.white}>
          <FormattedMessage id={`${PAGE_NAME_ID}.list.title`} />
        </Typography>
        <Box pt={5} pb={5}>
          <Box display={'flex'} justifyContent={!!filteredRows.length ? 'space-between' : 'end'} alignItems={'center'}>
            {filteredRows.length > 0 && (
              <Switch
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.selectAllFeeds` })}
                value={selectAllFeeds[currentPage] || false}
                onChange={(event) => handleSelectAllFeeds(currentPage, event.target.checked)}
              />
            )}
            <Box display={'flex'} justifyContent={'start'} alignItems={'center'}>
              {filteredRows.length > 0 && (
                <Dialog
                  maxWidth="md"
                  ref={disconnectFeedsDialogRef}
                  actions={
                    <Button
                      type="submit"
                      size="small"
                      color="secondary"
                      onClick={handleDisconnectBestMixFeeds}
                      disabled={disconnectBestMixFeeds.isPending}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.disconnectDialog.disconnectBtn`} />
                    </Button>
                  }
                  title={intl.formatMessage({ id: `${PAGE_NAME_ID}.disconnectDialog.title` })}
                  button={
                    <Button
                      style={{ marginBottom: 10, marginRight: 10 }}
                      size="small"
                      fullWidth={isMobile}
                      disabled={!selectAllFeeds[currentPage] && selectedFeeds.length === 0}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.disconnectBtn`} />
                    </Button>
                  }>
                  <Box>
                    <Typography pb={5} pt={5}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.disconnectDialog.disconnectLabel`} />
                    </Typography>
                    <Box>{renderSelectedFeeds(selectedFeeds)}</Box>
                  </Box>
                </Dialog>
              )}

              <Dialog
                ref={dialogRef}
                maxWidth="sm"
                title={intl.formatMessage({ id: `${PAGE_NAME_ID}.linkProductDialog.title` })}
                button={
                  <Button style={{ marginBottom: 10 }} size="small" fullWidth={isMobile}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.linkProductDialog.openBtn`} />
                  </Button>
                }>
                <form onSubmit={handleSubmit(handleLinkCustomer)}>
                  <FlexBox>
                    {linkCustomer.isPending && <LoadingSpinner overlay />}
                    <Autocomplete
                      dark
                      fullWidth
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.linkProductDialog.productsLabel` })}
                      options={_.chain(products)
                        .sortBy((p) => p.description.toLowerCase())
                        .filter((p) => p.active && p.hasMandatoryNutrients)
                        .value()}
                      getOptionLabel={(option) => String(option.description)}
                      controller={{
                        name: 'product',
                        control: control,
                        defaultValue: null,
                        rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                      }}
                    />
                    <TextField
                      dark
                      fullWidth
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.linkProductDialog.customerPricePerToneLabel` })}
                      controller={{
                        name: 'customerPricePerTone',
                        control: control,
                        rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                      }}
                    />
                    <Box display={'flex'} justifyContent={'end'} gap={4}>
                      <Button type="submit" size="medium">
                        <FormattedMessage id="general.save" />
                      </Button>
                    </Box>
                  </FlexBox>
                </form>
              </Dialog>
            </Box>
          </Box>
        </Box>
        <List
          options={[
            { id: 'checkbox', label: '', autoWidth: true },
            { id: 'description', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.descriptionLabel` }) },
            { id: 'updatedDate', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.updatedDateLabel` }) },
            {
              id: 'customerPricePerTon',
              label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.customerPricePerTonLabel` }),
              align: 'right',
            },
            {
              id: 'hasMandatoryNutrients',
              label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.hasMandatoryNutrientsLabel` }),
              align: 'center',
            },
            {
              id: 'hasIncorrectBestMixNutrientValues',
              label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.hasIncorrectBestMixNutrientValuesLabel` }),
              align: 'center',
            },
            { id: 'actions', label: '', align: 'right', autoWidth: true },
          ]}
          orderBy="name"
          isLoading={isFetching}
          noResult={{
            show: !filteredRows.length,
            text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
          }}>
          {filteredRows.slice(from, to).map((row) => (
            <ListRow key={row.code}>
              <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Checkbox
                  disableRipple
                  color="primary"
                  tabIndex={-1}
                  checked={selectedFeeds.includes(row.code)}
                  icon={<Square size={22} color={theme.palette.darkBlue.main} />}
                  checkedIcon={<SquareCheckBig size={22} color={theme.palette.darkBlue.main} />}
                  onChange={(_, checked) => handleSelectFeeds(row.code, checked)}
                />
              </ListCell>
              <ListCell>
                <span style={{ whiteSpace: 'break-spaces' }}>{row.description}</span>
              </ListCell>
              <ListCell>
                <FormattedDate value={row.updatedDate} day="2-digit" month="short" year="numeric" />
              </ListCell>
              <ListCell align="right">
                <FormattedNumber value={row.customerPricePerTon} style={`currency`} currency="USD" />
              </ListCell>
              <ListCell align={isMobile ? 'right' : 'center'}>
                {!row.hasMandatoryNutrients && (
                  <Box>
                    <AlertTriangle
                      fill={theme.palette.warning.light}
                      color={theme.palette.darkBlue.main}
                      size={18}
                      style={{ marginTop: 4 }}
                    />
                  </Box>
                )}
              </ListCell>
              <ListCell align={isMobile ? 'right' : 'center'}>
                <Box>
                  {row.hasIncorrectBestMixNutrientValues && (
                    <AlertTriangle
                      fill={theme.palette.warning.light}
                      color={theme.palette.darkBlue.main}
                      size={18}
                      style={{ marginTop: 4 }}
                    />
                  )}
                </Box>
              </ListCell>
              <ListCell align="right">
                <Dialog
                  ref={dialogRef}
                  maxWidth="xs"
                  title={`Unlink (${row.description})`}
                  button={
                    <Box>
                      <Button size="small">
                        <FormattedMessage id={`${PAGE_NAME_ID}.unlinkBtn`} />
                      </Button>
                    </Box>
                  }>
                  <Box display={'flex'} flexDirection={'column'} gap={4}>
                    <Box>
                      <FormattedMessage id="general.deleteComment" />
                    </Box>
                    <Box display={'flex'} justifyContent={'end'} gap={4}>
                      <Button variant="outlined" type="submit" size="small" onClick={() => dialogRef.current?.close()}>
                        <FormattedMessage id="general.cancel" />
                      </Button>
                      <Button onClick={() => handleUnlinkProduct(row.code)} size="small">
                        <FormattedMessage id={`${PAGE_NAME_ID}.unlinkBtn`} />
                      </Button>
                    </Box>
                  </Box>
                </Dialog>
              </ListCell>
            </ListRow>
          ))}
        </List>
        <Pagination currentPage={currentPage} pages={Math.ceil(filteredRows.length / PAGINATION_LIMIT)} onClick={handlePagination} />
      </Paper>
    </>
  );
};

export default EditCustomer;
